import { ViewIds } from '@ardoq/api-types';
import {
  EXPORT_FORMAT,
  type ExportLabels,
  type ExportTrackingData,
  composeExportMetadata,
  exportInFlight,
  getExportProfilingTransaction,
  isExportCapturing,
  notifyExportFailure,
  reportExportFailure,
  trackViewExport,
  viewExportCommands,
} from '@ardoq/export';
import { logError } from '@ardoq/logging';
import { trackExportingVisualization } from 'tracking/events/visualizations';
import * as profiling from '@ardoq/profiling';

type ExportBody = (args: {
  transaction: profiling.Transaction;
  mutableTrackingData: ExportTrackingData;
}) => Promise<void>;

type RunViewExportArgs = {
  viewId: ViewIds;
  format: EXPORT_FORMAT;
  isViewpointMode: boolean;
  labels: ExportLabels;
  /**
   * The format-specific work: render and save. Throw to report a failure — the
   * user sees the failure modal and the error reaches logging.
   */
  run: ExportBody;
};

/**
 * Exports run one at a time. They share process-wide state — the legend's
 * `forExport` class, added and removed around an await by both formats, and the
 * per-view exporting flag — so two overlapping exports can serialize each other's
 * legend or clear each other's flag. Nothing gates the Save as menu while an
 * export is in flight, and the diagram-sized ones take a while, so the pair is
 * reachable by clicking twice.
 */
const exports = { pending: Promise.resolve() };

/**
 * The envelope every canvas-rendered view export shares: profiling, tracking, the
 * export-started/finished pair, and turning a failure into something the user can
 * see. `pngExporter` and `svgExporter` differ only in what they render and save,
 * so this owns everything else — the two drifted apart twice while it was
 * duplicated between them.
 */
const queueExport = (args: RunViewExportArgs): Promise<void> => {
  const next = exports.pending.then(() => runExport(args));
  // Keep the chain alive past a rejection, which `runExport` handles rather than
  // propagates; this only guards against a future throw outside its try.
  exports.pending = next.catch(() => undefined);
  return next;
};

export const runViewExport = exportInFlight(queueExport);

const runExport = async ({
  viewId,
  format,
  isViewpointMode,
  labels,
  run,
}: RunViewExportArgs): Promise<void> => {
  const transaction = getExportProfilingTransaction();
  const mutableTrackingData = composeExportMetadata(
    { name: viewId, isViewpointMode },
    format
  );

  viewExportCommands.notifyExportStarted(viewId);
  trackViewExport(trackExportingVisualization, {
    name: viewId,
    format,
    isViewpointMode,
  });

  try {
    await run({ transaction, mutableTrackingData });
    viewExportCommands.notifyExportSucceeded(viewId);
  } catch (error) {
    logError(error as Error, `Failed to export the view to ${format}`, {
      viewId,
    });
    const capturing = isExportCapturing();
    reportExportFailure(error);
    if (!capturing) {
      notifyExportFailure(format, labels);
    }
  } finally {
    profiling.endTransaction(transaction, {
      viewId,
      metadata: mutableTrackingData,
    });
    viewExportCommands.notifyExportFinished(viewId);
  }
};
